/**
 * REST wire contracts (request + response DTOs) — the SINGLE source of truth
 * for the shape of every REST call between the web SPA and the Lambda API.
 *
 * WHY THIS EXISTS: the SPA (`@rufus/web`) and the API (`@rufus/lambda`) used to
 * each declare their own request/response types. They drifted, and every drift
 * was a runtime bug found only by exercising a screen (wrong response wrapping,
 * a missing required request field, a mis-shaped field). Defining each contract
 * ONCE here — imported by both sides — turns that whole class of bug into a
 * COMPILE error instead of a production surprise.
 *
 * TWO KINDS OF TYPE:
 *  - `*WireRequest` — EXACTLY what the client sends in the request body. It does
 *    NOT include server-injected identity (the authenticated `sub` the API
 *    derives from the Cognito authorizer: `creatorUserId`, `ownerUserId`,
 *    `userId`, `initiatorUserId`) nor server-only resolved values (e.g. the dice
 *    endpoint resolves `characterId` → a full authorized character server-side).
 *    The lambda's server request type is `WireRequest & { <injectedField> }`.
 *  - `*Response` — EXACTLY the JSON body a 2xx returns (the `json(2xx, {...})`
 *    envelope), so the client's parse is checked against what the server emits.
 *
 * Path parameters (e.g. `{campaignId}` in `/campaigns/{campaignId}/bring-in`)
 * are part of the URL, not the body; where the server ALSO reads a field from
 * the body it is included in the wire request. Response envelopes are wrapped
 * exactly as the handlers emit them (`{ campaigns }`, `{ character }`, …).
 */
// ---------------------------------------------------------------------------
// SPA error telemetry (R-telemetry) — client-crash ingestion wire contract
// ---------------------------------------------------------------------------
/**
 * Per-payload size caps on a telemetry report. The telemetry endpoint is
 * AUTHENTICATED (behind the same Cognito authorizer as every other route — there
 * is no anonymous path), so the primary abuse control is auth itself. These caps
 * are defense-in-depth that bound the size of ANY SINGLE accepted payload: the
 * server truncates/rejects anything larger so a buggy or malicious authenticated
 * client cannot store an unbounded record. They do NOT bound request RATE or
 * de-duplicate — per-payload size only. Shared so the client self-limits to the
 * same numbers before it ever sends.
 */
export const TELEMETRY_LIMITS = {
    /** Max breadcrumbs carried with one error (the client ring buffer size). */
    maxBreadcrumbs: 25,
    /** Max chars of the error message kept. */
    maxMessageLen: 1_000,
    /** Max chars of the stack trace kept. */
    maxStackLen: 8_000,
    /** Max chars of any single breadcrumb's message/detail. */
    maxBreadcrumbLen: 300,
    /** Max chars of a URL field (page url / breadcrumb target). */
    maxUrlLen: 500,
};
//# sourceMappingURL=rest-dtos.js.map